import { Badge } from '@ctw/shared/components/badge';
import { tw } from '@ctw/shared/utils/tailwind';

export interface SourceDocumentHeaderProps {
  title: string;
  clinicalDateDisplay?: string;
  badgeLabel?: string;
  sourceLine?: string;
  receivedDisplay?: string;
}

// The title/date/badge, network/provider, and received-date lines shared by every source
// document row across the Encounter and EpisodeOfCare drawers. Callers supply their own
// clickable wrapper and body content (narrative, sub-documents, view/download actions).
export const SourceDocumentHeader = ({
  title,
  clinicalDateDisplay,
  badgeLabel,
  sourceLine,
  receivedDisplay,
}: SourceDocumentHeaderProps) => (
  <div className={tw`flex flex-col items-start justify-center gap-0.5`}>
    <div className={tw`flex flex-wrap items-center gap-2`}>
      <span className={tw`font-medium text-[1rem] text-content-main tracking-wide`}>
        {clinicalDateDisplay ? `${title}, ${clinicalDateDisplay}` : title}
      </span>
      {badgeLabel && <Badge variant="meta" size="sm" content={badgeLabel} />}
    </div>
    {sourceLine && (
      <div className={tw`text-content-subtle text-xs leading-tight`}>{sourceLine}</div>
    )}
    {receivedDisplay && (
      <div className={tw`text-content-subtle text-xs leading-tight`}>
        Received {receivedDisplay}
      </div>
    )}
  </div>
);

// Joins the given parts with a middle dot, dropping any part that's missing or blank so a
// caller never renders a stray separator next to an empty value.
export const joinWithMiddleDot = (...parts: Array<string | undefined>): string | undefined => {
  const nonEmptyParts = parts.filter(
    (part): part is string => part !== undefined && part.trim().length > 0,
  );
  return nonEmptyParts.length > 0 ? nonEmptyParts.join(' · ') : undefined;
};
